﻿export const Align = {
  begin: 0,
  center: 1,
  end: 2
};

/**
 * The DrawingEngine is responsible for creating the product drawing. This is just an abstract base
 * class and serves the purpose of showing which methods must be implemented by concrete subclasses.
 */
export class DrawingEngine {
  constructor(name, options) {
    this.name = name || 'Drawing Engine';
    /* The productDrawing is the html element which hosts the display objects representing the
     * product and its components. For a canvas-based engine (Pixi, Babylon), the productDrawing is
     * the <canvas>. For an html-based engine (HtmlDrawingEngine), the productDrawing is the <div>
     * element which is the parent of the root display object. */
    this.productDrawing = options.productDrawing;
  }

  static createDefaultOptions(productDrawing) {
    //throw Error('createDefaultOptions must be implemented by the subclass');
    return {
      productDrawing
    };
  }

  /**
   * This method should return true if the engine class can share the material image loader with other
   * engines. This method should be reimplemented by subclasses.
   */
  static canShareLoader() { return false; }

  createDrawingService(getFinish) {
    throw Error('createDrawingService must be implemented by the subclass');
  }

/**
 * Pre-loads material images
 * @param {any} allMaterials Our global list of known materials
 * @param {any} materialIdsToLoad Material id's of the images we want to preload now.
 * @param {any} resolution Which set images to load. The value should be the name of the folder
 * containing the images in that resolution, e.g. 'low-res' or 'high-res'.
 */
  async loadMaterials(allMaterials, materialIdsToLoad, resolution) {
    throw Error('loadMaterials must be implemented by the subclass');
  }

  renderOnce() {
    throw Error('renderOnce must be implemented by the subclass');
  }

  /**
   * The owner class (e.g. ProductDrawing) can call this method to get the display object to be
   * associated with the stage product. The stage product is a code created root product instance
   * and the parent of the product being displayed/edited.
   */
  getStageDisplayObject() {
    throw Error('getStageDisplayObject must be implemented by the subclass');
  }

  /** Needed if the user want's to move the html element into a full screen display. */
  getRootHtmlElement(rootDisplayObject) {
    throw Error('getRootHtmlElementmust be implemented by the subclass');
  }

  /**
   * Is responsible for adding a display object to the drawing. Typically this only needs to happen
   * for the root display object, and with some engine this is already done by the fact that the
   * display objects were added to the drawing when they were instantiated (Pixi, Babylon). These
   * subclasses may therefore implement this method with a do-nothing statement.
   * @param {any} displayObject The html element, mesh or sprite representing the (root) product component
   */
  addToDrawing(displayObject) {
    throw Error('addToDrawing must be implemented by the subclass');
  }

  /**
   * Turns interaction on/off for the displayObject.
   * @param {displayObject} displayObject The object whose interaction is toggled.
   * @param {bool} on Whether to turn interaction on or off.
   * @param {callback} clickHandler Handler for the displayObject's click event.
   */
  toggleDisplayObjectInteractive(displayObject, on, clickHandler) {
    throw Error('toggleDisplayObjectInteractive must be implemented by the subclass');
  }

  /**
   * Returns an object with the width and height of the drawing. This should match the size of the
   * image returned by the getDrawingImageUrl method.
   */
  getDrawingSize() {
    throw Error('getDrawingSize must be implemented by the subclass');
  }

  getDrawingImageUrl() {
    throw Error('getDrawingImageUrl must be implemented by the subclass');
  }

  bestFitStageAndCanvas(fitCanvasAroundStage, renderOnce) {
    throw Error('bestFitStageAndCanvas must be implemented by the subclass');
  }

  resizeCanvasToFitInFullWindow(renderOnce) {
    throw Error('resizeCanvasToFitInFullWindow must be implemented by the subclass');
  }

  // As of 8/17/2020 not in use, but should still be functional
  resizeCanvasToFitInFullScreen(renderOnce) {
    throw Error('resizeCanvasToFitInFullScreen must be implemented by the subclass');
  }

  resizeCanvasToFitAroundStage(renderOnce) {
    throw Error('resizeCanvasToFitAroundStage must be implemented by the subclass');
  }
}

/** Each drawing engine should register themselves with this registry. */
export const EngineRegistry = {
  register: function(name, engineClass) {
    this[name] = engineClass;
  },
};